
import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Security, LoginCallback, useOktaAuth } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import PlanRegistration from './components/PlanRegistration';
import PlanSearch from './components/PlanSearch';
import { config } from './config';
import './App.css';
import { ErrorBoundary } from 'react-error-boundary';
import OktaLogin from './components/OktaLogin';
import { tokenManager } from './utils/tokenManager';
import { useDispatch } from 'react-redux';
import { forceResetSearchResults } from './redux/slices/planSlice';
import { startIdleTimer, stopIdleTimer, handleIdle } from './utils/idleTimer';
import EnrollmentDatesPortal from './components/EnrollmentDatesPortal';
import { Reports } from './components/Reports';
import FileAccess from './components/FileAccess';
import CensusDataUploader from './components/CensusDataUploader';

export const AuthContext = React.createContext<{
  token: string | null;
  isAuthenticated: boolean;
  authType: 'aws' | 'okta' | null;
  setAuthType: React.Dispatch<React.SetStateAction<'aws' | 'okta' | null>>;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  setToken: React.Dispatch<React.SetStateAction<string | null>>;
  getAuthHeaders: () => { Authorization: string; 'X-Auth-Type': 'aws' | 'okta' | null };
}>({
  token: null,
  isAuthenticated: false,
  authType: null,
  setAuthType: () => {},
  setIsAuthenticated: () => {},
  setToken: () => {},
  getAuthHeaders: () => ({ Authorization: '', 'X-Auth-Type': null }),
});

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.aws.userPoolId,
      userPoolClientId: config.aws.userPoolWebClientId,
    }
  }
});

// Configure Okta
const oktaAuth = new OktaAuth({
  issuer: config.okta.issuer,
  clientId: config.okta.clientId,
  redirectUri: config.okta.redirectUri,
  scopes: config.okta.scopes,
  pkce: true
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = React.useContext(AuthContext);
  const location = useLocation();

  // LOCAL DEV BYPASS - COMMENTED OUT FOR PRODUCTION DEPLOYMENT
  // WARNING: Uncomment these lines ONLY for local development testing
  // if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
  //   return <>{children}</>;
  // }
  // END LOCAL DEV BYPASS

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authType, setAuthType] = useState<'aws' | 'okta' | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { oktaAuth } = useOktaAuth();
  const dispatch = useDispatch();

  const setAuthState = useCallback((type: 'aws' | 'okta' | null, newToken: string | null) => {
    setIsAuthenticated(!!newToken);
    setAuthType(type);
    setToken(newToken);
    if (type && newToken) {
      tokenManager.setToken(newToken, type);
      tokenManager.setAuthType(type);
    } else {
      tokenManager.clearTokens();
    }
  }, []);

  const checkAuth = useCallback(async () => {
    console.log('checkAuth: Starting auth check');
    setIsLoading(true);
    
    // LOCAL DEV BYPASS - COMMENTED OUT FOR PRODUCTION DEPLOYMENT
    // WARNING: Uncomment these lines ONLY for local development testing
    // if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
    //   console.log('🔧 LOCAL DEV: Bypassing authentication for localhost');
    //   setAuthState('aws', 'fake-local-token-for-dev');
    //   setIsLoading(false);
    //   return;
    // }
    // END LOCAL DEV BYPASS
    
    try {
      const storedAuthType = tokenManager.getAuthType();
      const storedToken = await tokenManager.getToken();
      console.log('checkAuth: Stored auth type:', storedAuthType);
      console.log('checkAuth: Has stored token:', !!storedToken);

      if (storedAuthType === 'okta' && storedToken) {
        console.log('checkAuth: Checking Okta authentication');
        const isAuthenticated = await oktaAuth.isAuthenticated();
        console.log('checkAuth: Okta isAuthenticated:', isAuthenticated);
        if (isAuthenticated) {
          console.log('checkAuth: Setting Okta auth state');
          setAuthState('okta', storedToken);
          return;
        }
      } else if (storedAuthType === 'aws' && storedToken) {
        console.log('checkAuth: Checking AWS authentication');
        try {
          await getCurrentUser();
          console.log('checkAuth: Setting AWS auth state');
          setAuthState('aws', storedToken);
          return;
        } catch (error) {
          console.error('checkAuth: Error verifying AWS user:', error);
        }
      }

      console.log('checkAuth: No valid auth found, clearing state');
      setAuthState(null, null);
    } catch (error) {
      console.error('checkAuth: Error during auth check:', error);
      setAuthState(null, null);
    } finally {
      setIsLoading(false);
    }
  }, [oktaAuth, setAuthState]);

  useEffect(() => {
    const handleAuth = async () => {
      console.log('App: Starting auth check');
      console.log('App: Current location:', window.location.href);
      console.log('App: Checking if login redirect:', oktaAuth.isLoginRedirect());
      
      if (oktaAuth.isLoginRedirect()) {
        try {
          console.log('App: Handling login redirect...');
          console.log('App: Token endpoint:', `${oktaAuth.options.issuer}/v1/token`);
          
          await oktaAuth.handleLoginRedirect();
          const accessToken = await oktaAuth.getAccessToken();
          console.log('App: Got access token:', !!accessToken);
          
          if (accessToken) {
            console.log('App: Setting auth state and navigating to dashboard');
            setAuthState('okta', accessToken);
            navigate('/dashboard');
          } else {
            throw new Error('No access token received from Okta');
          }
        } catch (error: any) {
          console.error('App: Error handling Okta login redirect:', error);
          if (error.error_description) {
            console.error('App: Okta error description:', error.error_description);
          }
          if (error.message) {
            console.error('App: Error message:', error.message);
          }
          setAuthState(null, null);
          navigate('/');
        }
      } else {
        console.log('App: Not a login redirect, checking existing auth');
        await checkAuth();
      }
    };

    handleAuth();
  }, [oktaAuth, checkAuth, location, navigate, setAuthState]);

  useEffect(() => {
    if (isAuthenticated) {
      startIdleTimer(() => handleIdle(navigate));
    } else {
      stopIdleTimer();
    }

    return () => {
      stopIdleTimer();
    };
  }, [isAuthenticated, navigate]);

  const handleLogin = async (username: string, password: string) => {
    try {
      const signInResult = await signIn({ username, password });
      if (signInResult.isSignedIn) {
        const session = await fetchAuthSession();
        const newToken = session.tokens?.idToken?.toString() || '';
        tokenManager.setToken(newToken, 'aws');
        setAuthState('aws', newToken);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error completing sign in:', error);
      alert('Error completing sign in. Please try again.');
    }
  };

  const handleLogout = async () => {
    if (authType === 'aws') {
      await signOut();
    } else if (authType === 'okta') {
      await oktaAuth.signOut();
    }
    tokenManager.clearTokens();
    setAuthState(null, null);
    stopIdleTimer();
    navigate('/');
  };

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'X-Auth-Type': authType,
  }), [token, authType]);

  useEffect(() => {
    if (location.pathname !== '/plan-search') {
      dispatch(forceResetSearchResults());
    }
  }, [location.pathname, dispatch]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{
      token,
      isAuthenticated,
      authType,
      setAuthType,
      setIsAuthenticated,
      setToken,
      getAuthHeaders: () => ({
        Authorization: `Bearer ${token}`,
        'X-Auth-Type': authType,
      }),
    }}>
      <Routes>
        <Route path="/" element={
          // LOCAL DEV BYPASS - COMMENTED OUT FOR PRODUCTION DEPLOYMENT
          // WARNING: Uncomment the line below ONLY for local development testing
          // (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') ? (
          //   <Navigate to="/dashboard" replace />
          // ) : 
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <LoginForm onLogin={handleLogin} error={null} />
          )
        } />
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="/login/okta" element={<OktaLogin oktaAuth={oktaAuth} />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/plan-registration" element={
          <ProtectedRoute>
            <PlanRegistration />
          </ProtectedRoute>
        } />
        <Route path="/plan-search" element={
          <ProtectedRoute>
            <PlanSearch key={location.pathname} />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/file-access" element={
          <ProtectedRoute>
            <FileAccess />
          </ProtectedRoute>
        } />
        <Route path="/enrollment-dates" element={
          <ProtectedRoute>
            <EnrollmentDatesPortal />
          </ProtectedRoute>
        } />
        <Route path="/census-uploader" element={
          <ProtectedRoute>
            <CensusDataUploader />
          </ProtectedRoute>
        } />
      </Routes>
    </AuthContext.Provider>
  );
}

function ErrorFallback({error}: {error: Error}) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
    </div>
  )
}

function App() {
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    console.log('restoreOriginalUri: Restoring original URI:', originalUri);
    console.log('restoreOriginalUri: Current location:', window.location.href);
    const newUrl = toRelativeUrl(originalUri || '/', window.location.origin);
    console.log('restoreOriginalUri: Redirecting to:', newUrl);
    window.location.replace(newUrl);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <AppContent />
        </Security>
      </Router>
    </ErrorBoundary>
  );
}

export default App;